import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import type { GroupPricingFragmentFragment } from '@/features/group-pricing/gql/_gen_/group-pricing.gql';
import { RefreshCw, RotateCcw } from 'lucide-react';

interface ResetOptionsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onResetToDefault: () => void;
	onResetToSaved: () => void;
	activeGroup: GroupPricingFragmentFragment | null;
}

export function ResetOptionsDialog({
	open,
	onOpenChange,
	onResetToDefault,
	onResetToSaved,
	activeGroup,
}: ResetOptionsDialogProps) {
	const hasActiveGroup = activeGroup !== null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:tw-max-w-md'>
				<DialogHeader>
					<DialogTitle>Reset Options</DialogTitle>
					<DialogDescription>
						Choose how you want to reset the form
					</DialogDescription>
				</DialogHeader>

				<div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-py-4'>
					<Button
						variant='outline'
						onClick={() => {
							onResetToDefault();
							onOpenChange(false);
						}}
						className='tw-h-auto tw-py-6 tw-flex tw-flex-col tw-gap-2 tw-justify-center tw-items-center'
					>
						<RefreshCw className='tw-h-6 tw-w-6' />
						<div className='tw-font-semibold'>Reset to Defaults</div>
						<div className='tw-text-xs tw-text-muted-foreground'>
							Reset all fields to default values
						</div>
					</Button>

					<Button
						variant='outline'
						onClick={() => {
							onResetToSaved();
							onOpenChange(false);
						}}
						disabled={!hasActiveGroup}
						className={`tw-h-auto tw-py-6 tw-flex tw-flex-col tw-gap-2 tw-justify-center tw-items-center ${!hasActiveGroup ? 'tw-opacity-50' : ''}`}
					>
						<RotateCcw className='tw-h-6 tw-w-6' />
						<div className='tw-font-semibold'>Reset to Saved</div>
						<div className='tw-text-xs tw-text-muted-foreground'>
							{hasActiveGroup
								? `Reset to saved state of "${activeGroup.group_name || 'Untitled Group'}"`
								: 'No saved group loaded'}
						</div>
					</Button>
				</div>

				<DialogFooter>
					<Button variant='secondary' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
